import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, ShieldCheck, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

export const PricingSection = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      eyebrow: "Tarifs",
      title: "Une montée en gamme claire, lisible et assumée",
      subtitle:
        "Starter couvre la réputation. Pro ajoute la visibilité locale. Business renforce le multi-établissements, l'historique et les analyses avancées.",
      perMonth: "/mois",
      recommended: "Le plus stratégique",
      trialNote: "Essai gratuit 14 jours. Sans engagement.",
      compareNote: "Les prix affichés correspondent à la base mensuelle par compte.",
      ctaStarter: "Choisir Starter",
      ctaPro: "Choisir Pro",
      ctaBusiness: "Choisir Business",
      plans: [
        {
          name: "Starter",
          label: "Réputation & avis",
          description: "Pour centraliser les avis, répondre plus vite et déployer une collecte QR propre.",
          price: "49€",
          icon: ShieldCheck,
          highlighted: false,
          cta: "Choisir Starter",
          link: "/signup?plan=starter",
          features: [
            { text: "Centralisation Google et pilotage réputation", included: true },
            { text: "Réponses IA avec validation humaine", included: true },
            { text: "QR code et collecte de feedback", included: true },
            { text: "Dashboard réputation", included: true },
            { text: "Brique complète Visibilité locale", included: false },
            { text: "Audit GBP, Visibilité locale et Concurrents locaux", included: false },
          ],
        },
        {
          name: "Pro",
          label: "Réputation + visibilité locale",
          description: "Le bon niveau pour piloter votre réputation et comprendre votre présence locale dans une seule plateforme.",
          price: "99€",
          icon: Sparkles,
          highlighted: true,
          cta: "Choisir Pro",
          link: "/signup?plan=pro",
          badge: "Le plus stratégique",
          features: [
            { text: "Tout Starter inclus", included: true },
            { text: "Audit GBP", included: true },
            { text: "Visibilité locale", included: true },
            { text: "Concurrents locaux", included: true },
            { text: "Multi-plateforme réputation", included: true },
            { text: "Analytics plus avancés", included: true },
          ],
        },
        {
          name: "Business",
          label: "Version avancée multi-établissements",
          description: "Pour les groupes, réseaux ou enseignes qui veulent un cockpit centralisé plus profond et plus premium.",
          price: "199€",
          icon: Building2,
          highlighted: false,
          cta: "Choisir Business",
          link: "/signup?plan=business",
          features: [
            { text: "Tout Pro inclus", included: true },
            { text: "Jusqu'à 5 établissements", included: true },
            { text: "Historique renforcé", included: true },
            { text: "Analyses plus avancées", included: true },
            { text: "Pilotage multi-sites", included: true },
            { text: "Fonctionnalités premium", included: true },
          ],
        },
      ],
    },
    en: {
      eyebrow: "Pricing",
      title: "A clear, readable and intentional upgrade path",
      subtitle:
        "Starter covers reputation. Pro adds local visibility. Business expands the model for multi-location teams, deeper history and more advanced analysis.",
      perMonth: "/month",
      recommended: "Best strategic fit",
      trialNote: "14-day free trial. No commitment.",
      compareNote: "Displayed pricing reflects the monthly base per account.",
      ctaStarter: "Choose Starter",
      ctaPro: "Choose Pro",
      ctaBusiness: "Choose Business",
      plans: [
        {
          name: "Starter",
          label: "Reputation & reviews",
          description: "For businesses that want to centralize reviews, speed up replies and launch clean QR collection.",
          price: "$49",
          icon: ShieldCheck,
          highlighted: false,
          cta: "Choose Starter",
          link: "/signup?plan=starter",
          features: [
            { text: "Google review centralization and reputation control", included: true },
            { text: "AI-assisted replies with human validation", included: true },
            { text: "QR code and feedback collection", included: true },
            { text: "Reputation dashboard", included: true },
            { text: "Full Local Visibility suite", included: false },
            { text: "GBP Audit, Local Visibility and Local Competitors", included: false },
          ],
        },
        {
          name: "Pro",
          label: "Reputation + local visibility",
          description: "The right level for businesses that want to manage reputation and understand local presence in one platform.",
          price: "$99",
          icon: Sparkles,
          highlighted: true,
          cta: "Choose Pro",
          link: "/signup?plan=pro",
          badge: "Best strategic fit",
          features: [
            { text: "Everything in Starter", included: true },
            { text: "GBP Audit", included: true },
            { text: "Local Visibility", included: true },
            { text: "Local Competitors", included: true },
            { text: "Multi-platform reputation management", included: true },
            { text: "More advanced analytics", included: true },
          ],
        },
        {
          name: "Business",
          label: "Advanced multi-location version",
          description: "For groups, networks and brands that need a more centralized, deeper and more premium operating cockpit.",
          price: "$199",
          icon: Building2,
          highlighted: false,
          cta: "Choose Business",
          link: "/signup?plan=business",
          features: [
            { text: "Everything in Pro", included: true },
            { text: "Up to 5 locations", included: true },
            { text: "Stronger historical depth", included: true },
            { text: "More advanced analysis", included: true },
            { text: "Multi-site operating control", included: true },
            { text: "Premium capabilities", included: true },
          ],
        },
      ],
    },
  };

  const page = content[language];

  return (
    <section id="pricing" className="py-20 md:py-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-3xl text-center"
        >
          <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
            {page.eyebrow}
          </Badge>
          <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
            {page.title}
          </h2>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.subtitle}</p>
        </motion.div>

        <div className="mx-auto mt-12 grid max-w-6xl gap-6 lg:grid-cols-3">
          {page.plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className={`relative rounded-[2rem] border p-8 shadow-sm ${
                plan.highlighted
                  ? "border-primary bg-card shadow-xl shadow-primary/10"
                  : "border-border bg-card"
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-6">
                  <Badge className="rounded-full px-4 py-1.5 text-xs font-medium">{plan.badge}</Badge>
                </div>
              )}

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <plan.icon className="h-5 w-5 text-primary" />
              </div>

              <div className="mt-6">
                <p className="text-sm font-medium text-primary">{plan.label}</p>
                <h3 className="mt-2 text-2xl font-semibold text-foreground">{plan.name}</h3>
                <p className="mt-3 min-h-[72px] text-base leading-7 text-muted-foreground">{plan.description}</p>
              </div>

              <div className="mt-6 flex items-end gap-2 border-t border-border pt-6">
                <span className="text-5xl font-semibold tracking-tight text-foreground">{plan.price}</span>
                <span className="pb-1 text-base text-muted-foreground">{page.perMonth}</span>
              </div>

              <ul className="mt-6 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature.text} className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${feature.included ? "bg-accent/10" : "bg-muted"}`}>
                      {feature.included ? (
                        <Check className="h-3.5 w-3.5 text-accent" strokeWidth={3} />
                      ) : (
                        <X className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={3} />
                      )}
                    </div>
                    <span className={`text-sm leading-6 ${feature.included ? "text-foreground" : "text-muted-foreground"}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Button asChild size="lg" className={`mt-8 h-12 w-full text-base ${plan.highlighted ? "btn-premium" : ""}`} variant={plan.highlighted ? "default" : "outline"}>
                <Link to={plan.link}>
                  {plan.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>

              <p className="mt-3 text-center text-xs text-muted-foreground">{page.trialNote}</p>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">{page.compareNote}</p>
      </div>
    </section>
  );
};
